import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { AsaasSeal } from '@/components/payments/AsaasSeal';
import { ASAAS_CONTRACT_CLAUSE, ASAAS_SUPPORT } from '@/lib/asaasDisclosure';

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="mx-auto max-w-3xl px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
          <h1 className="text-2xl font-bold text-foreground">Termos de uso</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Última atualização: 22 de janeiro de 2025
          </p>
        </div>

        {/* Content */}
        <div className="rounded-xl border border-border bg-card p-6 space-y-6 text-foreground">
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">1. Aceitação dos termos</h2>
            <p className="text-muted-foreground leading-relaxed">
              Ao baixar, instalar ou usar o aplicativo Orçamigo ("App"), você concorda com estes 
              Termos de Uso. Se você não concorda com qualquer parte destes termos, não instale 
              ou use o App. Estes termos constituem um acordo legal vinculante entre você e a 
              equipe Orçamigo.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">2. Descrição do serviço</h2>
            <p className="text-muted-foreground leading-relaxed">
              O Orçamigo é um aplicativo de gestão de orçamentos profissionais que permite:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li>Criar e gerenciar orçamentos, pedidos e outros documentos comerciais</li>
              <li>Cadastrar e organizar clientes e produtos/serviços</li>
              <li>Gerar documentos em PDF para compartilhamento</li>
              <li>Compartilhar orçamentos via link público para aprovação de clientes</li>
              <li>Receber notificações sobre visualizações e aprovações</li>
              <li>Gerenciar múltiplas empresas (funcionalidade PRO)</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">3. Elegibilidade</h2>
            <p className="text-muted-foreground leading-relaxed">
              Para usar o Orçamigo, você deve ter pelo menos 18 anos de idade ou a maioridade 
              legal em sua jurisdição. Ao usar o App, você declara que possui capacidade legal 
              para celebrar este acordo.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">4. Cadastro e conta</h2>
            <p className="text-muted-foreground leading-relaxed">
              Para utilizar todas as funcionalidades do Orçamigo, você deve criar uma conta fornecendo:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li><strong>E-mail e senha:</strong> Para autenticação direta</li>
              <li><strong>Login com Google:</strong> Opcionalmente, usando sua conta Google</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-2">
              Você é responsável por manter a confidencialidade de suas credenciais e por todas 
              as atividades realizadas em sua conta. Notifique-nos imediatamente sobre qualquer 
              uso não autorizado.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">5. Permissões do aplicativo</h2>
            <p className="text-muted-foreground leading-relaxed">
              O Orçamigo pode solicitar as seguintes permissões do dispositivo:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li><strong>Câmera:</strong> Para capturar fotos de produtos, logos de empresas e documentos</li>
              <li><strong>Galeria/armazenamento:</strong> Para selecionar e salvar imagens e PDFs gerados</li>
              <li><strong>Contatos:</strong> Para salvar informações de clientes diretamente nos contatos do dispositivo (opcional)</li>
              <li><strong>Notificações:</strong> Para enviar alertas sobre visualizações e aprovações de orçamentos</li>
              <li><strong>Internet:</strong> Para sincronização de dados e funcionamento do serviço</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-2">
              Você pode revogar qualquer permissão a qualquer momento através das configurações 
              do seu dispositivo, porém algumas funcionalidades podem ficar indisponíveis.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">6. Uso aceitável</h2>
            <p className="text-muted-foreground leading-relaxed">
              Você concorda em usar o Orçamigo apenas para fins legais e comerciais legítimos. 
              É expressamente proibido:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li>Usar o App para atividades ilegais, fraudulentas ou enganosas</li>
              <li>Criar orçamentos falsos ou com informações enganosas</li>
              <li>Tentar acessar dados ou contas de outros usuários sem autorização</li>
              <li>Interferir no funcionamento do App ou de sua infraestrutura</li>
              <li>Fazer engenharia reversa, descompilar ou modificar o App</li>
              <li>Usar o App para distribuir malware, spam ou conteúdo ofensivo</li>
              <li>Violar direitos de propriedade intelectual de terceiros</li>
              <li>Revender ou redistribuir o acesso ao App sem autorização</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">7. Planos e pagamentos</h2>
            <p className="text-muted-foreground leading-relaxed">
              O Orçamigo oferece:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li><strong>Plano gratuito:</strong> Funcionalidades básicas com cota de 30 orçamentos por mês, renovada todo dia 1º</li>
              <li><strong>Plano PRO:</strong> Funcionalidades completas, sem limites, via assinatura</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-2">
              <strong>Pagamentos no App (Android):</strong> As assinaturas são processadas através 
              do Google Play e gerenciadas pelo sistema de compras do Google. Ao assinar:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li>A cobrança será feita através da sua conta Google Play</li>
              <li>A assinatura renova automaticamente ao final de cada período</li>
              <li>Você pode cancelar a qualquer momento nas configurações do Google Play</li>
              <li>O cancelamento entra em vigor no próximo ciclo de cobrança</li>
              <li>Não há reembolsos para períodos parciais, exceto conforme exigido por lei</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-2">
              <strong>Pagamentos na Web:</strong> As assinaturas via navegador são processadas 
              pelo Stripe com as mesmas regras de renovação e cancelamento.
            </p>
          </section>

          {/*
            Seção EXIGIDA pela Resolução Conjunta nº 16/2025 e pelo playbook de
            BaaS do Asaas. O texto das três cláusulas é o MODELO fornecido por
            eles e mora em `asaasDisclosure.ts`: o tomador não pode alterar nem
            criar documento legal sobre o processamento financeiro, então
            reescrever aqui seria violação, não melhoria.
          */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">8. Serviços financeiros e de pagamentos</h2>
            {ASAAS_CONTRACT_CLAUSE.map((paragrafo) => (
              <p key={paragrafo.slice(0, 40)} className="text-muted-foreground leading-relaxed">
                {paragrafo}
              </p>
            ))}
            <div className="pt-1">
              <AsaasSeal />
            </div>
            <p className="text-muted-foreground leading-relaxed mt-2">
              <strong>Suporte do Asaas:</strong> para dúvidas e problemas relacionados às operações
              financeiras de pagamento (conta de pagamento, cobranças, transferências), você pode
              acionar o Asaas diretamente pelo telefone {ASAAS_SUPPORT.phone} (
              {ASAAS_SUPPORT.phoneNote}) ou pelo e-mail {ASAAS_SUPPORT.email}.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">9. Conteúdo do usuário</h2>
            <p className="text-muted-foreground leading-relaxed">
              Você mantém todos os direitos sobre os dados que insere no Orçamigo, incluindo 
              informações de clientes, produtos, orçamentos e imagens. Ao usar o App, você 
              nos concede uma licença limitada para armazenar, processar e exibir esse conteúdo 
              exclusivamente para fornecer o serviço.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-2">
              Você é o único responsável pelo conteúdo que cria e compartilha, garantindo que 
              possui os direitos necessários e que não viola leis ou direitos de terceiros.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">10. Propriedade intelectual</h2>
            <p className="text-muted-foreground leading-relaxed">
              O Orçamigo, incluindo sua marca, design, código-fonte, funcionalidades e documentação, 
              é protegido por direitos autorais e outras leis de propriedade intelectual. Você 
              recebe apenas uma licença limitada, não exclusiva e revogável para usar o App 
              conforme estes termos.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">11. Disponibilidade do serviço</h2>
            <p className="text-muted-foreground leading-relaxed">
              Nos esforçamos para manter o Orçamigo disponível 24/7, mas não garantimos 
              disponibilidade ininterrupta. O serviço pode ser temporariamente indisponível 
              devido a manutenções, atualizações ou circunstâncias fora do nosso controle.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">12. Limitação de responsabilidade</h2>
            <p className="text-muted-foreground leading-relaxed">
              O Orçamigo é fornecido "como está" e "conforme disponível", sem garantias de 
              qualquer tipo, expressas ou implícitas. Não nos responsabilizamos por:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li>Perdas ou danos decorrentes do uso ou impossibilidade de uso do App</li>
              <li>Perda de dados, lucros cessantes ou danos indiretos</li>
              <li>Erros em orçamentos ou cálculos (você deve sempre revisar antes de enviar)</li>
              <li>Ações de terceiros que acessem orçamentos via links públicos</li>
              <li>Interrupções no serviço ou falhas técnicas</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-2">
              Em qualquer caso, nossa responsabilidade máxima será limitada ao valor pago por 
              você nos últimos 12 meses.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">13. Indenização</h2>
            <p className="text-muted-foreground leading-relaxed">
              Você concorda em indenizar e isentar a equipe Orçamigo de quaisquer reclamações, 
              danos, perdas ou despesas decorrentes de: (a) seu uso do App; (b) violação destes 
              termos; (c) violação de direitos de terceiros; ou (d) conteúdo que você criar 
              ou compartilhar através do App.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">14. Modificações dos termos</h2>
            <p className="text-muted-foreground leading-relaxed">
              Podemos atualizar estes Termos de Uso periodicamente. Alterações significativas 
              serão notificadas através do App ou por e-mail com pelo menos 30 dias de 
              antecedência. O uso continuado após as alterações constitui aceitação dos novos 
              termos. Se você não concordar, deve parar de usar o App.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">15. Rescisão</h2>
            <p className="text-muted-foreground leading-relaxed">
              <strong>Por você:</strong> Você pode encerrar sua conta a qualquer momento através 
              das Configurações do App ou solicitando via e-mail. Cancele qualquer assinatura 
              ativa antes de excluir a conta.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-2">
              <strong>Por nós:</strong> Podemos suspender ou encerrar sua conta se você violar 
              estes termos, com ou sem aviso prévio dependendo da gravidade.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-2">
              Após a rescisão, você perderá acesso aos dados armazenados. Recomendamos exportar 
              seus dados antes de encerrar a conta.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">16. Lei aplicável e foro</h2>
            <p className="text-muted-foreground leading-relaxed">
              Estes termos são regidos pelas leis da República Federativa do Brasil. Qualquer 
              disputa será submetida ao foro da comarca de São Paulo, SP, com renúncia a 
              qualquer outro, por mais privilegiado que seja.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">17. Disposições gerais</h2>
            <p className="text-muted-foreground leading-relaxed">
              Se qualquer disposição destes termos for considerada inválida, as demais 
              permanecerão em vigor. Nossa falha em exercer qualquer direito não constitui 
              renúncia. Estes termos constituem o acordo integral entre você e o Orçamigo 
              quanto ao uso do App.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">18. Contato</h2>
            <p className="text-muted-foreground leading-relaxed">
              Para dúvidas, sugestões ou reclamações sobre estes Termos de Uso:
            </p>
            <ul className="list-none text-muted-foreground space-y-1 ml-4">
              <li><strong>E-mail:</strong> contato@orcamigo.com.br</li>
              <li><strong>Suporte:</strong> suporte@orcamigo.com.br</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
